import { create } from 'zustand'
import { api, ApiRequestError } from '../lib/api'

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

export interface DuplicateMember {
  id: string
  employer: string
  role_title: string
  period: string | null
  competency_tags: string[]
  context_tags: string[]
  // The neutral description (not CV copy) — shown so the user can judge whether
  // two proofs are genuinely the same. Truncated for display when long; the
  // full text lives on the proof in the Library.
  canonical_description: string | null
  description_truncated: boolean
  authority_notes: string | null
  role_context: string | null
}

// Per-member relationship to the group's survivor, curated by the server
// (_serialise_disposition). The internal `relationship` enum is never shown in
// the UI — the card renders the derived booleans as plain words. A
// `recommended_action` of 'resolve_conflict' marks a contradiction (not
// mergeable). Fold-input text (proposed_addition/follow_on) never crosses to the
// client, per Hard Rule 3.
export interface DuplicateDisposition {
  member_id: string
  relationship: string
  recommended_action: 'merge' | 'keep_separate' | 'resolve_conflict'
  confidence: number | null
  conflict_description: string | null
  adds_detail: boolean
  adds_perspective: boolean
}

export interface DuplicateGroup {
  group_id: string
  members: DuplicateMember[]
  max_similarity: number
  suggested_action: 'merge' | 'keep_all'
  merge_confidence: number | null
  // Resolved-cluster fields from the unified classifier. survivor_id is the
  // representative every other member was classified against (the default
  // primary); dispositions carry the per-member merge/keep recommendation.
  survivor_id: string | null
  dispositions: DuplicateDisposition[]
}

interface AuditState {
  groups: DuplicateGroup[]
  loading: boolean
  error: string | null
}

interface AuditActions {
  fetchGroups: () => Promise<void>
  // Relationship-driven group merge (resolve-screen redesign): fold the chosen
  // members into the primary; the server derives each fold type from the cached
  // dispositions. Members omitted from mergeMemberIds are kept separate and stay
  // eligible for future audits.
  applyMerge: (groupId: string, primaryId: string, mergeMemberIds: string[]) => Promise<boolean>
  // keep_all / dismiss the group via the legacy resolve endpoint.
  resolve: (action: 'keep_all' | 'dismiss', groupId: string) => Promise<void>
  reset: () => void
}

// ────────────────────────────────────────────────────────────────────────────
// Store
// ────────────────────────────────────────────────────────────────────────────

const initialState: AuditState = {
  groups: [],
  loading: false,
  error: null,
}

const errorMessage = (err: unknown): string =>
  err instanceof ApiRequestError ? err.error.message : 'Something went wrong.'

export const useAuditStore = create<AuditState & AuditActions>((set) => ({
  ...initialState,

  fetchGroups: async () => {
    set({ loading: true, error: null })
    try {
      const res = await api.get<{ data: { groups: DuplicateGroup[] } }>(
        '/api/v1/library/audit/duplicates'
      )
      set({ groups: res.data.groups, loading: false })
    } catch (err) {
      set({ loading: false, error: errorMessage(err) })
    }
  },

  applyMerge: async (groupId, primaryId, mergeMemberIds) => {
    set({ error: null })
    try {
      await api.post('/api/v1/library/audit/duplicates/apply', {
        group_id: groupId,
        primary_id: primaryId,
        merge_member_ids: mergeMemberIds,
      })
      // The group is resolved — drop it from local state.
      set((state) => ({ groups: state.groups.filter((g) => g.group_id !== groupId) }))
      return true
    } catch (err) {
      set({ error: errorMessage(err) })
      return false
    }
  },

  resolve: async (action, groupId) => {
    set({ error: null })
    try {
      await api.post('/api/v1/library/audit/duplicates/resolve', {
        action,
        group_id: groupId,
        member_ids: [],
        winner_id: null,
      })
      set((state) => ({ groups: state.groups.filter((g) => g.group_id !== groupId) }))
    } catch (err) {
      set({ error: errorMessage(err) })
    }
  },

  reset: () => set({ ...initialState }),
}))
